import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { apiFetch, ApiError, type ApiFetchOptions } from "./apiFetch.ts";
import { useSelector } from "react-redux";
import {
  hasOlderUnsentMutation,
  queueMutation,
  markAwaiting,
  markFailed,
  markRetryable,
  markSending,
} from "../Offline/pendingMutationsDb.ts";
import { flushPendingMutations, isTerminalFailure } from "../Offline/flushPendingMutations.ts";
import type { AppAuthState } from "../Redux/AppAuthReducer.ts";
import { useOperations } from "../Operations/OperationsContext.ts";
import { getOriginApp } from "../App/originApp.ts";

const DEFAULT_CONFIRM_TIMEOUT_MS = 10000;

export class OperationFailedError extends Error {}

function isTransportFailure(error: unknown): boolean {
  return !(error instanceof ApiError) && !(error instanceof OperationFailedError);
}

export interface UseApiMutationOptions<TData, TVariables> extends Omit<
  UseMutationOptions<TData, Error, TVariables>,
  "mutationFn"
> {
  url: string;
  method?: "POST" | "PUT" | "DELETE";
  offlineCapable?: boolean;
  skipAuthRefresh?: boolean;
  /**
   * Human-readable label for the global operations tracker. When provided, the
   * mutation is registered in the operations store (avatar ring + list) and
   * resolved when the consumer confirms over WebSocket. Connection is owned by
   * the app-level OperationsProvider.
   */
  operationLabel?: string;
  /**
   * When true, the mutation resolves only after the backend consumer confirms
   * processing over WebSocket (OPERATION_COMPLETED). OPERATION_FAILED rejects the
   * mutation; a confirmation timeout resolves optimistically. An offlineCapable
   * mutation keeps its outbox entry either way, so an unconfirmed write is not lost.
   */
  confirmViaWebSocket?: boolean;
  confirmTimeoutMs?: number;
}

/**
 * operationUuid is the idempotency key for this mutation.
 *
 * CRITICAL INVARIANT: operationUuid MUST be stable across retries.
 * - Generated ONCE at first call (not on each retry)
 * - Persisted to Dexie queue on offline (survives page reloads)
 * - Used by BE processed_operations for dedup
 *
 * Never regenerate UUID on retry — that would defeat idempotency.
 */
export function useApiMutation<TData = unknown, TVariables extends object = object>(
  options: UseApiMutationOptions<TData, TVariables>,
): UseMutationResult<TData, Error, TVariables> {
  const {
    url,
    method = "POST",
    offlineCapable = false,
    skipAuthRefresh = false,
    operationLabel,
    confirmViaWebSocket = false,
    confirmTimeoutMs = DEFAULT_CONFIRM_TIMEOUT_MS,
    ...rest
  } = options;

  const { trackOperation, failOperation, waitForOperation } = useOperations();
  const userUuid = useSelector((s: { auth: AppAuthState }) => s.auth.user?.sub);

  return useMutation<TData, Error, TVariables>({
    ...rest,
    // react-query pauses mutations while the browser reports no connection, so mutationFn
    // would never run and the offline queue below could never be reached.
    networkMode: "always",
    mutationFn: async (vars: TVariables) => {
      const operationUuid =
        (vars as { operationUuid?: string }).operationUuid ?? crypto.randomUUID();
      const body = { ...vars, operationUuid, app: getOriginApp() };
      const fetchOptions: ApiFetchOptions = { method, body, skipAuthRefresh };

      // The outbox is written first so a request that never gets a response still exists
      // afterwards; a 202 only means the API queued it, not that anything was written.
      let createdAt = Date.now();
      if (offlineCapable) {
        createdAt = await queueMutation({
          operationUuid,
          userUuid,
          label: operationLabel,
          endpoint: url,
          method,
          body,
        });
      }

      if (operationLabel) trackOperation(operationUuid, operationLabel);

      if (
        offlineCapable &&
        (await hasOlderUnsentMutation({ operationUuid, endpoint: url, createdAt }))
      ) {
        void flushPendingMutations();
        return { operationUuid, status: "queued" } as unknown as TData;
      }

      const request = async (): Promise<TData> => {
        if (offlineCapable) await markSending(operationUuid);
        return apiFetch<TData>(url, fetchOptions);
      };

      // Anything queued behind this one for the same endpoint is waiting on it, and the flush has
      // no other way of learning that this send finished.
      const releaseQueueBehind = (): void => {
        if (offlineCapable) void flushPendingMutations();
      };

      try {
        if (confirmViaWebSocket) {
          const confirmation = waitForOperation(operationUuid, confirmTimeoutMs);
          const result = await request();
          if (offlineCapable) await markAwaiting(operationUuid);
          releaseQueueBehind();
          if ((await confirmation) === "failed") throw new OperationFailedError();
          return result;
        }
        const result = await request();
        if (offlineCapable) await markAwaiting(operationUuid);
        releaseQueueBehind();
        return result;
      } catch (error) {
        if (offlineCapable && isTransportFailure(error)) {
          await markRetryable(operationUuid, 0);
          releaseQueueBehind();
          return { operationUuid, status: "queued" } as unknown as TData;
        }
        if (offlineCapable && error instanceof ApiError) {
          if (isTerminalFailure(error, 0)) await markFailed(operationUuid, 0);
          else await markRetryable(operationUuid, 1);
          releaseQueueBehind();
        }
        if (operationLabel) failOperation(operationUuid);
        throw error;
      }
    },
  });
}
